import React from 'react';
import { Phone, Scale, BookOpen, Truck, Users, AlertTriangle } from 'lucide-react';
export function GvwPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2360">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2361">
        <div className="container mx-auto px-4" data-id="element-2362">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2363">
            GVW Definitions
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2364">
            Commercial Motor Vehicle Weight & Classification Standards
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2365">
        <div className="max-w-4xl mx-auto" data-id="element-2366">
          {/* What Definitions Are Used */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2367">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2368">
              <BookOpen className="mr-3 text-[#B1050B]" size={28} data-id="element-2369" />
              What Definitions Are Used in This Part?
            </h2>

            <div className="space-y-6" data-id="element-2370">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2371">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2372">
                  Commercial Motor Vehicle (CMV) Definition
                </h3>
                <p className="text-gray-700 mb-4" data-id="element-2373">
                  A commercial motor vehicle is defined by any of the following
                  criteria:
                </p>

                <div className="space-y-4" data-id="element-2374">
                  <div className="bg-white rounded-lg p-4" data-id="element-2375">
                    <div className="flex items-start" data-id="element-2376">
                      <span className="bg-blue-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2377">
                        (1)
                      </span>
                      <div data-id="element-2378">
                        <p className="font-bold text-gray-900 mb-2" data-id="element-2379">
                          Weight Rating
                        </p>
                        <p className="text-gray-700" data-id="element-2380">
                          A gross vehicle weight (GVW), gross vehicle weight
                          rating (GVWR), gross combination weight (GCW), or
                          gross combination weight rating (GCWR) of{' '}
                          <strong data-id="element-2381">
                            4,537 kilograms (10,001 pounds) or more
                          </strong>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4" data-id="element-2382">
                    <div className="flex items-start" data-id="element-2383">
                      <span className="bg-green-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2384">
                        (2)
                      </span>
                      <div data-id="element-2385">
                        <p className="font-bold text-gray-900 mb-2" data-id="element-2386">
                          Passenger Capacity
                        </p>
                        <p className="text-gray-700" data-id="element-2387">
                          Regardless of weight, is designed or used to transport{' '}
                          <strong data-id="element-2388">
                            16 or more passengers, including driver
                          </strong>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4" data-id="element-2389">
                    <div className="flex items-start" data-id="element-2390">
                      <span className="bg-red-600 text-white font-bold px-3 py-1 rounded mr-4 flex-shrink-0" data-id="element-2391">
                        (3)
                      </span>
                      <div data-id="element-2392">
                        <p className="font-bold text-gray-900 mb-2" data-id="element-2393">
                          Hazardous Materials
                        </p>
                        <p className="text-gray-700" data-id="element-2394">
                          Regardless of weight, is used in the transportation of
                          hazardous materials and is required to be placarded
                          pursuant to{' '}
                          <strong data-id="element-2395">49 CFR part 172, subpart F</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Weight Definitions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2396">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2397">
              <Scale className="mr-3 text-[#B1050B]" size={28} data-id="element-2398" />
              Weight Classification Definitions
            </h2>

            <div className="space-y-6" data-id="element-2399">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2400">
                <h3 className="font-bold text-blue-900 mb-3 text-xl" data-id="element-2401">
                  GVW (Gross Vehicle Weight)
                </h3>
                <p className="text-gray-700" data-id="element-2402">
                  The actual weight of a vehicle and its load
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-2403">
                <h3 className="font-bold text-green-900 mb-3 text-xl" data-id="element-2404">
                  GVWR (Gross Vehicle Weight Rating)
                </h3>
                <p className="text-gray-700" data-id="element-2405">
                  The maximum weight a vehicle is designed to carry, as
                  specified by the manufacturer
                </p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6" data-id="element-2406">
                <h3 className="font-bold text-yellow-900 mb-3 text-xl" data-id="element-2407">
                  GCW (Gross Combination Weight)
                </h3>
                <p className="text-gray-700" data-id="element-2408">
                  The actual total weight of a combination of vehicles (e.g.,
                  truck and trailer)
                </p>
              </div>

              <div className="bg-purple-50 rounded-lg p-6" data-id="element-2409">
                <h3 className="font-bold text-purple-900 mb-3 text-xl" data-id="element-2410">
                  GCWR (Gross Combination Weight Rating)
                </h3>
                <p className="text-gray-700" data-id="element-2411">
                  The maximum weight a combination of vehicles is designed to
                  carry, as specified by the manufacturer
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 mt-6" data-id="element-2412">
                <p className="font-bold text-gray-900 mb-2" data-id="element-2413">
                  Critical Threshold:
                </p>
                <p className="text-gray-700" data-id="element-2414">
                  The key weight threshold for CMV classification is{' '}
                  <strong data-id="element-2415">10,001 pounds (4,537 kg) or more</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Key Program Definitions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2416">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2417">
              Key Program Definitions
            </h2>

            <div className="space-y-6" data-id="element-2418">
              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2419">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2420">
                  Commercial Vehicle Safety Plan (CVSP)
                </h3>
                <p className="text-gray-700" data-id="element-2421">
                  The document outlining the State's CMV safety objectives,
                  strategies, activities and performance measures.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2422">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2423">
                  Compatible or Compatibility
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-2424">
                  State laws and regulations applicable to interstate commerce
                  and to intrastate movement of hazardous materials are
                  identical to the FMCSRs and the HMRs or have the same effect
                  as the FMCSRs.
                </p>
                <p className="text-gray-700" data-id="element-2425">
                  State laws applicable to intrastate commerce are either
                  identical to, or have the same effect as, the FMCSRs or fall
                  within the established limited variances under §350.341.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2426">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2427">
                  High Priority Activity Funds
                </h3>
                <p className="text-gray-700" data-id="element-2428">
                  Funds provided to States, local governments, and other persons
                  carrying out activities and projects that directly support the
                  MCSAP, are national in scope in that the successful activity
                  or project could potentially be applied in other States on a
                  national scale, and improve CMV safety and compliance with CMV
                  safety regulations. Up to <strong data-id="element-2429">5 percent</strong> of total
                  MCSAP funds are available for these activities.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2430">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2431">
                  Incentive Funds
                </h3>
                <p className="text-gray-700" data-id="element-2432">
                  Funds awarded to States achieving reductions in CMV involved
                  fatal accidents, CMV fatal accident rate, or meeting specified
                  CMV safety program performance criteria.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2433">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2434">
                  Operating Authority
                </h3>
                <p className="text-gray-700" data-id="element-2435">
                  The registration required by 49 U.S.C. 13902, 49 CFR part 365,
                  49 CFR part 368, and 49 CFR 392.9a.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2436">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2437">Large Truck</h3>
                <p className="text-gray-700" data-id="element-2438">
                  A truck over{' '}
                  <strong data-id="element-2439">10,000 pounds gross vehicle weight rating</strong>{' '}
                  including single unit trucks and truck tractors (FARS
                  definition).
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2440">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2441">Motor Carrier</h3>
                <p className="text-gray-700" data-id="element-2442">
                  A for-hire motor carrier or private motor carrier. The term
                  includes a motor carrier's agents, officers, or
                  representatives responsible for hiring, supervising, training,
                  assigning, or dispatching a driver or concerned with the
                  installation, inspection, and maintenance of motor vehicle
                  equipment or accessories or both.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6" data-id="element-2443">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2444">
                  North American Standard Inspection
                </h3>
                <p className="text-gray-700" data-id="element-2445">
                  The methodology used by State CMV safety inspectors to conduct
                  safety inspections of CMVs. This consists of various levels of
                  inspection of the vehicle or driver or both. The inspection
                  criteria are developed by the FMCSA in conjunction with the
                  Commercial Vehicle Safety Alliance (CVSA), an association of
                  States, Canadian Provinces, and Mexico whose members agree to
                  adopt these standards for inspecting CMVs in their
                  jurisdiction.
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mt-6 italic" data-id="element-2446">
              [65 FR 15102, Mar. 21, 2000; 71 FR 50866, Aug. 28, 2006]
            </p>
          </div>

          {/* Quick Reference */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2447">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2448">
              Quick Reference: CMV Classification
            </h2>

            <div className="space-y-4" data-id="element-2449">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-2450">
                <div className="flex items-start" data-id="element-2451">
                  <Scale className="mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2452" />
                  <div data-id="element-2453">
                    <p className="font-bold mb-2" data-id="element-2454">Weight Threshold</p>
                    <p className="text-gray-200" data-id="element-2455">
                      10,001+ pounds (4,537+ kg) GVW/GVWR/GCW/GCWR
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2456">
                <div className="flex items-start" data-id="element-2457">
                  <Users className="mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2458" />
                  <div data-id="element-2459">
                    <p className="font-bold mb-2" data-id="element-2460">Passenger Capacity</p>
                    <p className="text-gray-200" data-id="element-2461">
                      16+ passengers (including driver), regardless of weight
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2462">
                <div className="flex items-start" data-id="element-2463">
                  <AlertTriangle className="mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2464" />
                  <div data-id="element-2465">
                    <p className="font-bold mb-2" data-id="element-2466">Hazardous Materials</p>
                    <p className="text-gray-200" data-id="element-2467">
                      Placarded hazmat (49 CFR 172, subpart F), regardless of
                      weight
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-2468">
                <div className="flex items-start" data-id="element-2469">
                  <Truck className="mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2470" />
                  <div data-id="element-2471">
                    <p className="font-bold mb-2" data-id="element-2472">Large Truck Definition</p>
                    <p className="text-gray-200" data-id="element-2473">
                      Over 10,000 lbs GVWR (single units and truck tractors)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2474">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2475">Key Takeaways</h2>

            <div className="space-y-3" data-id="element-2476">
              <div className="flex items-start" data-id="element-2477">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2478">✓</span>
                <span data-id="element-2479">
                  CMV defined by weight (10,001+ lbs), passenger capacity (16+),
                  or placarded hazmat
                </span>
              </div>
              <div className="flex items-start" data-id="element-2480">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2481">✓</span>
                <span data-id="element-2482">
                  GVW = actual weight; GVWR = manufacturer's maximum rating
                </span>
              </div>
              <div className="flex items-start" data-id="element-2483">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2484">✓</span>
                <span data-id="element-2485">
                  GCW = actual combination weight; GCWR = manufacturer's
                  combination rating
                </span>
              </div>
              <div className="flex items-start" data-id="element-2486">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2487">✓</span>
                <span data-id="element-2488">
                  Large truck = over 10,000 lbs GVWR (FARS definition)
                </span>
              </div>
              <div className="flex items-start" data-id="element-2489">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2490">✓</span>
                <span data-id="element-2491">
                  North American Standard Inspection developed by FMCSA and CVSA
                </span>
              </div>
              <div className="flex items-start" data-id="element-2492">
                <span className="mr-3 flex-shrink-0 mt-1" data-id="element-2493">✓</span>
                <span data-id="element-2494">State laws must be compatible with FMCSRs and HMRs</span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2495">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2496">
              Need Help Understanding GVW Requirements?
            </h2>
            <p className="text-xl mb-6" data-id="element-2497">
              Expert guidance on weight classifications and CMV compliance
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2498">
              <Phone className="mr-3" size={24} data-id="element-2499" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-2500">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}